import { Camera, ChevronLeft, ChevronRight, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { TaskPhoto } from '../types/task';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent } from './ui/dialog';

interface TaskPhotosSectionProps {
  taskId: string;
}

const TaskPhotosSection: React.FC<TaskPhotosSectionProps> = ({ taskId }) => {
  const [photos, setPhotos] = useState<TaskPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    loadPhotos();
  }, [taskId]);

  const loadPhotos = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/${taskId}/photos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        setPhotos(data.data || []);
      } else {
        setError(data.message || 'Failed to load photos');
      }
    } catch (err) {
      console.error('Error loading photos:', err);
      setError('Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = (index: number) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') goToPrevious();
    if (e.key === 'ArrowRight') goToNext();
    if (e.key === 'Escape') setLightboxOpen(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const currentPhoto = photos[currentIndex];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Camera className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">Photos</CardTitle>
            {!loading && photos.length > 0 && (
              <Badge variant="secondary">{photos.length}</Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="aspect-square bg-muted animate-pulse rounded-lg"
              />
            ))}
          </div>
        ) : error ? (
          <div className="text-sm text-destructive py-4 text-center">{error}</div>
        ) : photos.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center">
            No photos attached to this task
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {photos.map((photo, index) => (
              <button
                key={photo.id}
                onClick={() => openLightbox(index)}
                className="aspect-square rounded-lg overflow-hidden border bg-muted hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                <img
                  src={photo.url}
                  alt={photo.original_filename || `Photo ${index + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        )}
      </CardContent>

      {/* Lightbox Modal */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent
          className="max-w-4xl p-0 bg-black/95 border-none"
          onKeyDown={handleKeyDown}
        >
          <div className="relative">
            {/* Close button */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 z-10 text-white hover:bg-white/20"
              onClick={() => setLightboxOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>

            {/* Navigation buttons */}
            {photos.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20"
                  onClick={goToPrevious}
                >
                  <ChevronLeft className="h-8 w-8" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20"
                  onClick={goToNext}
                >
                  <ChevronRight className="h-8 w-8" />
                </Button>
              </>
            )}

            {/* Image */}
            {currentPhoto && (
              <div className="flex flex-col">
                <div className="flex items-center justify-center min-h-[60vh] max-h-[75vh] p-4">
                  <img
                    src={currentPhoto.url}
                    alt={currentPhoto.original_filename || 'Task photo'}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>

                {/* Photo metadata */}
                <div className="px-4 py-3 bg-black/80 text-white text-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-1">
                      {currentPhoto.original_filename && (
                        <span className="font-medium">{currentPhoto.original_filename}</span>
                      )}
                      <span className="text-white/70">
                        {formatDate(currentPhoto.created_at)}
                        {currentPhoto.uploaded_by_user && (
                          <> by {currentPhoto.uploaded_by_user.first_name} {currentPhoto.uploaded_by_user.last_name}</>
                        )}
                      </span>
                    </div>
                    {photos.length > 1 && (
                      <span className="text-white/70">
                        {currentIndex + 1} / {photos.length}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default TaskPhotosSection;
